import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '@/components/ui/accordion';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '@/components/ui/select';
import { Ruler, Palette, Wrench, Eye, Frame, Layers, ArrowLeftRight } from 'lucide-react';

export const ControlPanel = ({ configuration, updateConfiguration }) => {
  const profiles = ['W20', 'W40', 'FPS50'];
  const colors = ['Black', 'White', 'Bronze', 'Desert Sand', 'Soft Pink', 'Flaxpod', 'Charcoal'];
  const hardwareOptions = ['Lever Handle (Standard)', 'No Hardware'];
  const glassTypes = ['Clear', 'Frosted', 'Fluted', 'Aquatex', 'Rain', 'Flemish'];
  const doorTypes = ['Single', 'Double'];
  const panelStyles = ['Horizontal Lites (3 Equal)', 'Horizontal Lites (3 - Small Middle)', 'Horizontal Lites (4 Equal)', 'Grid Pattern (2x2)', 'Grid Pattern (3x2)'];
  const transomOptions = ['No', 'Yes'];
  const thresholdOptions = ['With Threshold', 'No Threshold'];
  const sideliteOptions = ['No', 'Yes'];
  const archedOptions = ['No', 'Yes'];

  return (
    <div className="p-6 md:p-8 space-y-2" data-testid="control-panel">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Configure Your Door
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Customize every detail to match your requirements
        </p>
      </div>

      <Accordion type="multiple" defaultValue={['profile', 'door-config', 'dimensions', 'panel-style', 'color', 'hardware', 'glass']} className="space-y-2">
        {/* Profile Selection */}
        <AccordionItem value="profile" className="border rounded-none" data-testid="profile-section">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <Frame className="w-5 h-5" />
              <span className="font-medium">Profile Type</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="grid grid-cols-3 gap-2 mt-2">
              {profiles.map(profile => (
                <button
                  key={profile}
                  data-testid={`profile-${profile}`}
                  onClick={() => updateConfiguration('profile', profile)}
                  className={`p-4 border rounded-none text-center font-mono text-sm hover:border-primary transition-colors ${
                    configuration.profile === profile 
                      ? 'border-primary bg-primary text-primary-foreground' 
                      : 'border-border'
                  }`}
                >
                  {profile}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Selected: <span className="font-mono font-medium">{configuration.profile}</span>
            </p>
          </AccordionContent>
        </AccordionItem>

        {/* Door Configuration */}
        <AccordionItem value="door-config" className="border rounded-none" data-testid="door-config-section">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5" />
              <span className="font-medium">Door Configuration</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-4">
            {/* Door Type */}
            <div className="space-y-2">
              <Label>Door Type</Label>
              <div className="grid grid-cols-2 gap-2">
                {doorTypes.map(type => (
                  <button
                    key={type}
                    data-testid={`door-type-${type}`}
                    onClick={() => updateConfiguration('door_type', type)}
                    className={`p-3 border rounded-none text-center text-sm hover:border-primary transition-colors ${
                      configuration.door_type === type 
                        ? 'border-primary bg-primary text-primary-foreground' 
                        : 'border-border'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Transom */}
            <div className="space-y-2">
              <Label>Transom (Top Window)</Label>
              <div className="grid grid-cols-2 gap-2">
                {transomOptions.map(option => (
                  <button
                    key={option}
                    data-testid={`transom-${option}`}
                    onClick={() => updateConfiguration('transom', option)}
                    className={`p-3 border rounded-none text-center text-sm hover:border-primary transition-colors ${
                      configuration.transom === option 
                        ? 'border-primary bg-primary text-primary-foreground' 
                        : 'border-border'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Transom Height - only show when transom is enabled */}
            {configuration.transom === 'Yes' && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="transomHeight">Transom Height (mm)</Label>
                  <Input
                    id="transomHeight"
                    data-testid="transom-height-input"
                    type="number"
                    value={configuration.transomHeight || 400}
                    onChange={(e) => updateConfiguration('transomHeight', parseFloat(e.target.value) || 400)}
                    className="w-24 h-8 text-sm rounded-none font-mono"
                    min={200}
                    max={800}
                  />
                </div>
                <Slider
                  data-testid="transom-height-slider"
                  value={[configuration.transomHeight || 400]}
                  onValueChange={(val) => updateConfiguration('transomHeight', val[0])}
                  min={200}
                  max={800}
                  step={50}
                  className="mt-2"
                />
              </div>
            )}

            {/* Sidelite Left */}
            <div className="space-y-2">
              <Label>Left Sidelite</Label>
              <div className="grid grid-cols-2 gap-2">
                {sideliteOptions.map(option => (
                  <button
                    key={option}
                    data-testid={`sidelite-left-${option}`}
                    onClick={() => updateConfiguration('sidelite_left', option)}
                    className={`p-3 border rounded-none text-center text-sm hover:border-primary transition-colors ${
                      configuration.sidelite_left === option 
                        ? 'border-primary bg-primary text-primary-foreground' 
                        : 'border-border'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Sidelite Right */}
            <div className="space-y-2">
              <Label>Right Sidelite</Label>
              <div className="grid grid-cols-2 gap-2">
                {sideliteOptions.map(option => (
                  <button
                    key={option}
                    data-testid={`sidelite-right-${option}`}
                    onClick={() => updateConfiguration('sidelite_right', option)}
                    className={`p-3 border rounded-none text-center text-sm hover:border-primary transition-colors ${
                      configuration.sidelite_right === option 
                        ? 'border-primary bg-primary text-primary-foreground' 
                        : 'border-border'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Arched in Square */}
            <div className="space-y-2">
              <Label>Arched in Square (Door Leafs Only)</Label>
              <div className="grid grid-cols-2 gap-2">
                {archedOptions.map(option => (
                  <button
                    key={option}
                    data-testid={`arched-${option}`}
                    onClick={() => updateConfiguration('arched_in_square', option)}
                    className={`p-3 border rounded-none text-center text-sm hover:border-primary transition-colors ${
                      configuration.arched_in_square === option 
                        ? 'border-primary bg-primary text-primary-foreground' 
                        : 'border-border'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* True Arch Frame */}
            <div className="space-y-2">
              <Label>True Arch Frame</Label>
              <div className="grid grid-cols-2 gap-2">
                {archedOptions.map(option => (
                  <button
                    key={`true-arch-${option}`}
                    data-testid={`true-arch-${option}`}
                    onClick={() => updateConfiguration('true_arch_frame', option)}
                    className={`p-3 border rounded-none text-center text-sm hover:border-primary transition-colors ${
                      configuration.true_arch_frame === option 
                        ? 'border-primary bg-primary text-primary-foreground' 
                        : 'border-border'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Dimensions */}
        <AccordionItem value="dimensions" className="border rounded-none" data-testid="dimensions-section">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <Ruler className="w-5 h-5" />
              <span className="font-medium">Dimensions</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-4">
            {/* Width */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="width">Width (mm)</Label>
                <Input
                  id="width"
                  data-testid="width-input"
                  type="number"
                  value={configuration.width}
                  onChange={(e) => updateConfiguration('width', parseFloat(e.target.value) || 800)}
                  className="w-24 h-8 text-sm rounded-none font-mono"
                  min={600}
                  max={1500}
                />
              </div>
              <Slider
                data-testid="width-slider"
                value={[configuration.width]}
                onValueChange={(val) => updateConfiguration('width', val[0])}
                min={600}
                max={1500}
                step={50}
                className="mt-2"
              />
            </div>

            {/* Height */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="height">Height (mm)</Label>
                <Input
                  id="height"
                  data-testid="height-input"
                  type="number"
                  value={configuration.height}
                  onChange={(e) => updateConfiguration('height', parseFloat(e.target.value) || 2000)}
                  className="w-24 h-8 text-sm rounded-none font-mono"
                  min={1800}
                  max={2800}
                />
              </div>
              <Slider
                data-testid="height-slider"
                value={[configuration.height]}
                onValueChange={(val) => updateConfiguration('height', val[0])}
                min={1800}
                max={2800}
                step={50}
                className="mt-2"
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Panel Style */}
        <AccordionItem value="panel-style" className="border rounded-none" data-testid="panel-style-section">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5" />
              <span className="font-medium">Panel Style</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-4">
            <div className="space-y-2">
              <Label>Style</Label>
              <Select 
                value={configuration.panel_style}
                onValueChange={(val) => updateConfiguration('panel_style', val)}
              >
                <SelectTrigger data-testid="panel-style-select" className="w-full rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {panelStyles.map(style => (
                    <SelectItem key={style} value={style} data-testid={`panel-style-${style}`}>
                      {style}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Lites Count (only show if Horizontal Lites selected) - REMOVED since it's fixed at 3 */}
            <p className="text-xs text-muted-foreground mt-2">
              {configuration.panel_style === 'Horizontal Lites (3 Divisions)' && '3 equal horizontal glass panels'}
              {configuration.panel_style === 'Grid Pattern (3x2)' && '6 glass panels in 3×2 grid'}
            </p>
          </AccordionContent>
        </AccordionItem>

        {/* Color/Finish */}
        <AccordionItem value="color" className="border rounded-none" data-testid="color-section">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              <span className="font-medium">Color & Finish</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <Select 
              value={configuration.color}
              onValueChange={(val) => updateConfiguration('color', val)}
            >
              <SelectTrigger data-testid="color-select" className="w-full rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {colors.map(color => (
                  <SelectItem key={color} value={color} data-testid={`color-${color}`}>
                    {color}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </AccordionContent>
        </AccordionItem>

        {/* Hardware */}
        <AccordionItem value="hardware" className="border rounded-none" data-testid="hardware-section">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <Wrench className="w-5 h-5" />
              <span className="font-medium">Hardware</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <Select 
              value={configuration.hardware}
              onValueChange={(val) => updateConfiguration('hardware', val)}
            >
              <SelectTrigger data-testid="hardware-select" className="w-full rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {hardwareOptions.map(hardware => (
                  <SelectItem key={hardware} value={hardware} data-testid={`hardware-${hardware}`}>
                    {hardware}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </AccordionContent>
        </AccordionItem>

        {/* Glass Options */}
        <AccordionItem value="glass" className="border rounded-none" data-testid="glass-section">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              <span className="font-medium">Glass Options</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <Select 
              value={configuration.glass_type}
              onValueChange={(val) => updateConfiguration('glass_type', val)}
            >
              <SelectTrigger data-testid="glass-select" className="w-full rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {glassTypes.map(glass => (
                  <SelectItem key={glass} value={glass} data-testid={`glass-${glass}`}>
                    {glass}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default ControlPanel;
